import { modelScale, modelSlugOf, modelSweptBox, } from './models.js';
import { POSTER_LIMITS, posterLayout } from './posters.js';
import { sceneStateAt } from './scene-state.js';
/**
 * The radius each builder uses when a lesson gives none. Here rather than in
 * the headset's builders so framing and drawing agree; `render/scene.ts`
 * imports them.
 */
export const DEFAULT_RADIUS = { star: 1, planet: 0.5, moon: 0.15, station: 0.4 };
/** How far an orbiting object bobs above and below its orbit (the headset's animation). */
export const ORBIT_BOB = 0.15;
const DEGREES = Math.PI / 180;
/** Moved, turned (degrees, XYZ as three.js's Euler) and scaled alike in every direction. */
export function compose(pos, rot, scale = 1) {
    const [x = 0, y = 0, z = 0] = pos ?? [];
    const [rx = 0, ry = 0, rz = 0] = rot ?? [];
    const a = Math.cos(rx * DEGREES);
    const b = Math.sin(rx * DEGREES);
    const c = Math.cos(ry * DEGREES);
    const d = Math.sin(ry * DEGREES);
    const e = Math.cos(rz * DEGREES);
    const f = Math.sin(rz * DEGREES);
    const s = scale;
    // three.js Matrix4.makeRotationFromEuler, order 'XYZ'.
    return [
        c * e * s,
        -c * f * s,
        d * s,
        x,
        (a * f + b * e * d) * s,
        (a * e - b * f * d) * s,
        -b * c * s,
        y,
        (b * f - a * e * d) * s,
        (b * e + a * f * d) * s,
        a * c * s,
        z,
    ];
}
/** `outer` applied after `inner`. */
export function multiply(outer, inner) {
    const m = (row, col) => outer[row * 4] * inner[col] +
        outer[row * 4 + 1] * inner[4 + col] +
        outer[row * 4 + 2] * inner[8 + col];
    const out = [];
    for (let row = 0; row < 3; row++) {
        out.push(m(row, 0), m(row, 1), m(row, 2), m(row, 3) + outer[row * 4 + 3]);
    }
    return out;
}
export function apply(t, p) {
    return [
        t[0] * p[0] + t[1] * p[1] + t[2] * p[2] + t[3],
        t[4] * p[0] + t[5] * p[1] + t[6] * p[2] + t[7],
        t[8] * p[0] + t[9] * p[1] + t[10] * p[2] + t[11],
    ];
}
/** A part's own box, about its own middle, before it is moved into place. */
export function leafBox(part) {
    const box = (hx, hy, hz) => ({
        min: [-hx, -hy, -hz],
        max: [hx, hy, hz],
    });
    switch (part.type) {
        case 'box':
            return box(part.size[0] / 2, part.size[1] / 2, part.size[2] / 2);
        case 'sphere':
            return box(part.size[0], part.size[0], part.size[0]);
        case 'cylinder': {
            const r = Math.max(part.size[0], part.size[1]);
            return box(r, part.size[2] / 2, r);
        }
        case 'cone':
            return box(part.size[0], part.size[1] / 2, part.size[0]);
        case 'capsule':
            return box(part.size[0], part.size[1] / 2 + part.size[0], part.size[0]);
        case 'torus': {
            const reach = part.size[0] + part.size[1];
            return box(reach, reach, part.size[1]);
        }
        case 'plane':
            return box(part.size[0] / 2, part.size[1] / 2, 0);
        case 'lathe': {
            // A negative distance from the axis is drawn as 0 (render/shapes.ts).
            const r = Math.max(0, ...part.points.map(([distance]) => distance));
            const heights = part.points.map(([, h]) => h);
            return { min: [-r, Math.min(...heights), -r], max: [r, Math.max(...heights), r] };
        }
        case 'extrude': {
            const xs = part.outline.map(([x]) => x);
            const ys = part.outline.map(([, y]) => y);
            return {
                min: [Math.min(...xs), Math.min(...ys), -part.depth / 2],
                max: [Math.max(...xs), Math.max(...ys), part.depth / 2],
            };
        }
    }
}
/** A running box, grown one point at a time; empty until the first. */
class Grow {
    min = [Infinity, Infinity, Infinity];
    max = [-Infinity, -Infinity, -Infinity];
    add(p) {
        for (let i = 0; i < 3; i++) {
            this.min[i] = Math.min(this.min[i], p[i]);
            this.max[i] = Math.max(this.max[i], p[i]);
        }
    }
    get empty() {
        return this.min[0] === Infinity;
    }
    get box() {
        return {
            min: [this.min[0], this.min[1], this.min[2]],
            max: [this.max[0], this.max[1], this.max[2]],
        };
    }
}
function corners(box) {
    const out = [];
    for (const x of [box.min[0], box.max[0]]) {
        for (const y of [box.min[1], box.max[1]]) {
            for (const z of [box.min[2], box.max[2]])
                out.push([x, y, z]);
        }
    }
    return out;
}
function collect(parts, parent, subject, obstacle) {
    for (const part of parts) {
        if (part.type === 'group') {
            collect(part.parts, multiply(parent, compose(part.pos, part.rot, part.scale)), subject, obstacle);
            continue;
        }
        const place = multiply(parent, compose(part.pos, part.rot));
        for (const corner of corners(leafBox(part))) {
            const p = apply(place, corner);
            obstacle.add(p);
            if (part.stand !== true)
                subject.add(p);
        }
    }
}
function shapeBounds(parts, state) {
    const place = compose(state.pos, state.rot, state.scale);
    const subject = new Grow();
    const obstacle = new Grow();
    collect(parts, place, subject, obstacle);
    if (obstacle.empty) {
        // No parts at all: the headset draws a 0.1 m grey block (render/shapes.ts).
        for (const corner of corners({ min: [-0.05, -0.05, -0.05], max: [0.05, 0.05, 0.05] })) {
            obstacle.add(apply(place, corner));
        }
    }
    // A shape that is all plinth is still something a step can be about.
    return { box: subject.empty ? obstacle.box : subject.box, obstacle: obstacle.box };
}
/**
 * A poster: its whole frame, a thin slab turned by its facing (`state.rot`,
 * set by sceneStateAt). Until its photo is known it is framed as 4:3.
 */
function posterBounds(object, state) {
    const { outer } = posterLayout(object);
    const place = compose(state.pos, state.rot);
    const grow = new Grow();
    const half = {
        min: [-outer.width / 2, -outer.height / 2, -POSTER_LIMITS.depth / 2],
        max: [outer.width / 2, outer.height / 2, POSTER_LIMITS.depth / 2],
    };
    for (const corner of corners(half))
        grow.add(apply(place, corner));
    return { box: grow.box, obstacle: grow.box };
}
/**
 * A library model (ADR-043): its box from the catalogue, in the model frame
 * (forward −z, on the ground, plan C1) -- grown by what `clips` sweep, when a
 * step's clips are given -- turned about y by its turn (`state.rot[1]`, set
 * by sceneStateAt), scaled by the size the lesson asks. Undefined when the
 * catalogue does not know the slug.
 */
function modelBounds(object, state, catalogue, clips) {
    const slug = modelSlugOf(object.kind);
    const facts = slug === undefined ? undefined : catalogue?.[slug];
    if (!facts)
        return undefined;
    const scale = modelScale(object, facts) * state.scale;
    const own = modelSweptBox(facts, clips);
    // A swimmer or flyer stands `above` the land (ADR-054): its box raised by that much.
    const above = object.above ?? 0;
    const place = compose([state.pos[0], state.pos[1] + above, state.pos[2]], [0, state.rot[1], 0], scale);
    const grow = new Grow();
    for (const corner of corners(own))
        grow.add(apply(place, corner));
    return { box: grow.box, obstacle: grow.box };
}
function roundBounds(round) {
    const [x, y, z] = round.centre;
    const box = {
        min: [x - round.radius, y - round.height, z - round.radius],
        max: [x + round.radius, y + round.height, z + round.radius],
    };
    return { box, obstacle: box, round };
}
/**
 * The room an object takes in a given state, or undefined when there is
 * nothing to frame: hidden, a bone (AR only, and AR has no framing), a
 * namespace this engine does not build, or a library model the `catalogue`
 * does not know (ADR-043: a model's size is the library's, not the lesson's).
 * A model's `clips` -- what it plays at the moment -- grow its box by what
 * they sweep (the lectern rule counts a bite's reach).
 */
export function objectBounds(object, state, catalogue, clips) {
    if (!state.visible)
        return undefined;
    const [namespace = ''] = object.kind.split(':');
    if (namespace === 'shape')
        return shapeBounds(object.parts ?? [], state);
    if (namespace === 'poster')
        return posterBounds(object, state);
    if (namespace === 'model')
        return modelBounds(object, state, catalogue, clips);
    if (!(namespace in DEFAULT_RADIUS))
        return undefined;
    const radius = (object.radius ?? DEFAULT_RADIUS[namespace]) * state.scale;
    if (object.parent !== undefined && object.orbit !== undefined) {
        // The whole orbit: framing waits for no moon.
        return roundBounds({
            centre: state.pos,
            radius: object.orbit + radius,
            height: ORBIT_BOB + radius,
        });
    }
    if (object.ring) {
        // A disc tilted with the planet: as wide as its rings, as tall as the
        // tilt lifts them, and never less than the planet itself. A ball of the
        // rings' radius would make Saturn 5.5 m tall and push the pupil back for
        // nothing.
        const outer = object.ring[1] * radius;
        const height = Math.max(radius, outer * Math.abs(Math.sin(object.tilt ?? 0)));
        return roundBounds({ centre: state.pos, radius: outer, height });
    }
    return roundBounds({ centre: state.pos, radius, height: radius });
}
/**
 * Each object's state as the lesson declares it, before any step changes it:
 * `hidden`, `rot` and `scale` as written; an orbiting object at its parent.
 */
export function declaredStates(lesson) {
    return sceneStateAt(lesson, -1);
}
/**
 * Every object that can be framed, by id, in the given states (declared by
 * default); a library model only with the `catalogue` that knows it.
 */
export function lessonBounds(lesson, states = declaredStates(lesson), catalogue) {
    const bounds = new Map();
    for (const [id, object] of Object.entries(lesson.objects ?? {})) {
        const state = states.get(id);
        const measured = state ? objectBounds(object, state, catalogue) : undefined;
        if (measured)
            bounds.set(id, measured);
    }
    return bounds;
}
function unionBox(boxes) {
    const grow = new Grow();
    for (const box of boxes) {
        grow.add(box.min);
        grow.add(box.max);
    }
    return grow.box;
}
/**
 * The smallest box around several objects: a step's look object and its focus.
 * One object is returned as it is, round or not.
 */
export function unionBounds(all) {
    if (all.length <= 1)
        return all[0];
    return {
        box: unionBox(all.map((b) => b.box)),
        obstacle: unionBox(all.map((b) => b.obstacle)),
    };
}
/** A box's middle. */
export function centreOf(box) {
    return [
        (box.min[0] + box.max[0]) / 2,
        (box.min[1] + box.max[1]) / 2,
        (box.min[2] + box.max[2]) / 2,
    ];
}
/** The eight corners of a box, for projecting it. */
export const boxCorners = corners;
//# sourceMappingURL=bounds.js.map